import { trapFocus, removeTrapFocus } from '../base/global';
import { CartItems } from '../modules/cart';
import { logError } from '../personalisation/src/utilities/logging';

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.setHeaderCartIconAccessibility();

    window.CartDrawer = {
      el: this,
      updateCart: this.updateCart.bind(this),
      close: this.close.bind(this),
      open: this.open.bind(this),
      updateCartDrawerContents: this.updateCartDrawerContents.bind(this)
    };

    window.addEventListener('DOMContentLoaded', () => {
      if (this.shouldOpenDrawer() === true) {
        this.open();
      }
    });
    // Prepare (but don't start) the DOM observer for grouping gift wrap rows.
  }

  shouldOpenDrawer() {
    const params = new URLSearchParams(document.location.search);
    const cartParam = params.get('cart');
    return cartParam === '1';
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (/** @type {KeyboardEvent} */ (event)?.code?.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Ensure lightbox listeners are bound when drawer opens.
    this.initLightbox();
  }

  close() {
    this?.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });
  }

  renderContents(parsedState) {
    if (this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHtml = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      sectionElement.innerHTML = sectionHtml;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();

      // Re-init lightbox after re-render.
      this.initLightbox();
      document.dispatchEvent(new CustomEvent('CART_DRAWER_UPDATED'));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsedElement ? parsedElement.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      }
      // {
      //   id: 'cart-icon-bubble'
      // }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Initialise lightbox interactions for cart property images.
   */
  initLightbox() {
    // Cache lightbox elements within this drawer instance.
    this.cartLightbox = this.querySelector('.js-cart-drawer-lightbox');
    if (!this.cartLightbox) return;

    const closeButton = this.cartLightbox.querySelector('.js-cart-lightbox-close');
    if (closeButton && !closeButton.__cartLightboxBound) {
      closeButton.addEventListener('click', () => this.closeLightbox());
      closeButton.__cartLightboxBound = true;
    }

    if (!this.__cartLightboxDelegated) {
      this.addEventListener('click', (event) => {
        const trigger = /** @type {Element|null} */ (
          event.target instanceof Element ? event.target.closest('.js-cart-lightbox-trigger') : null
        );
        if (trigger && trigger instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();
          const imageSrc = trigger.getAttribute('data-image-src') || trigger.getAttribute('src');
          const imageAlt = trigger.getAttribute('data-image-alt') || trigger.getAttribute('alt') || 'Cart image';
          if (imageSrc) this.openLightbox(imageSrc, imageAlt);
        }
      });
      this.__cartLightboxDelegated = true;
    }

    if (!this.__cartLightboxOverlayBound) {
      this.cartLightbox.addEventListener('click', (event) => {
        if (event.target === this.cartLightbox) {
          this.closeLightbox();
        }
      });
      this.__cartLightboxOverlayBound = true;
    }

    if (!this.__cartLightboxEscBound) {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.cartLightbox && this.cartLightbox.style.display === 'flex') {
          this.closeLightbox();
        }
      });
      this.__cartLightboxEscBound = true;
    }
  }

  /**
   * Open the lightbox with a given image.
   * @param {string} imageSrc
   * @param {string} imageAlt
   */
  openLightbox(imageSrc, imageAlt) {
    if (!this.cartLightbox) return;
    if (this.cartLightbox instanceof HTMLElement && this.cartLightbox.style.display === 'flex') return;
    const lightboxImage = this.cartLightbox.querySelector('.js-cart-lightbox-image');
    if (lightboxImage && lightboxImage instanceof HTMLImageElement) {
      lightboxImage.src = imageSrc;
      lightboxImage.alt = imageAlt || 'Cart image';
    }
    this.cartLightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  /** Close the lightbox. */
  closeLightbox() {
    if (!this.cartLightbox) return;
    this.cartLightbox.style.display = 'none';
    document.body.style.overflow = '';
  }

  async updateCart({ html } = { html: null }) {
    let theHtml = html;
    let theCartCount = 0;
    try {
      const response = await fetch('/cart.js', { method: 'GET' });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      theCartCount = cartData.items.reduce((acc, item) => acc + item.quantity, 0);

      // Update cart count in header/icon
      const cartCountBubbleItems = document.querySelector('.cart-count-bubble .visually-hidden');
      const cartCountBubbleItemsCounter = document.querySelector('.cart-count-bubble [aria-hidden="true"]');

      if (cartCountBubbleItems) {
        const [, cartCountBubbleItemsLabel] = cartCountBubbleItems.textContent?.split(' ') ?? [];
        cartCountBubbleItems.textContent = `${cartCountBubbleItemsLabel} ${theCartCount}`;
      }

      if (cartCountBubbleItemsCounter) {
        cartCountBubbleItemsCounter.textContent = `${theCartCount}`;
      }

      // Update cart drawer contents if it's open
      await this.updateCartDrawerContents({ theHtml });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  async updateCartDrawerContents({ theHtml } = { theHtml: null }) {
    try {
      if (!theHtml) {
        const response = await fetch('?sections=cart-drawer', { method: 'GET' });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const cartData = await response.json();
        theHtml = cartData['cart-drawer'];
        if (!theHtml) {
          throw new Error('No HTML returned from cart.js');
        }
      }
      // Parse the response and selectively update only the cart items and footer
      const parsedHtml = new DOMParser().parseFromString(theHtml, 'text/html');

      // Update cart-drawer-items (cart items list)
      const targetCartItems = document.querySelector('.js-cart-drawer-items-area');
      const sourceCartItems = parsedHtml.querySelector('.js-cart-drawer-items-area');
      const targetEmptyArea = document.querySelector('.js-drawer__inner-empty-area');
      const sourceEmptyArea = parsedHtml.querySelector('.js-drawer__inner-empty-area');

      if (targetCartItems && sourceCartItems) {
        // Cart has items, update the items area
        targetCartItems.replaceWith(sourceCartItems);
      } else if (sourceCartItems && !targetCartItems) {
        // Cart now has items but didn't before, replace empty area with items
        if (targetEmptyArea) {
          targetEmptyArea.replaceWith(sourceCartItems);
        }
      } else if (!sourceCartItems && targetCartItems) {
        // Cart is now empty but had items before, remove items area
        targetCartItems.remove();
      }

      // Handle empty state area
      if (sourceEmptyArea && !targetEmptyArea) {
        // Cart is now empty, add empty state area
        const drawerHeader = document.querySelector('.drawer__header');
        if (drawerHeader && drawerHeader.nextElementSibling) {
          drawerHeader.nextElementSibling.insertAdjacentElement('afterend', sourceEmptyArea);
        }
      } else if (targetEmptyArea && sourceEmptyArea) {
        // Update existing empty state
        targetEmptyArea.replaceWith(sourceEmptyArea);
      } else if (targetEmptyArea && !sourceEmptyArea) {
        // Cart now has items, remove empty state
        targetEmptyArea.remove();
      }

      // Update cart drawer footer (threshold, totals, etc.)
      const targetFooter = document.querySelector('.js-drawer__footer-area');
      const sourceFooter = parsedHtml.querySelector('.js-drawer__footer-area');

      if (targetFooter && sourceFooter) {
        targetFooter.replaceWith(sourceFooter);
      } else {
        logError({
          label: 'updateCartDrawerContents',
          error: new Error('Footer update failed'),
          message: 'Footer update failed',
          extraData: { targetFooter: !!targetFooter, sourceFooter: !!sourceFooter }
        });
      }
      // Update cart drawer empty state classes
      const cartDrawer = document.querySelector('.js-cart-drawer-area');
      const sourceCartDrawer = parsedHtml.querySelector('.js-cart-drawer-area');
      if (cartDrawer && sourceCartDrawer) {
        // Update empty state classes
        cartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
      }
    } catch (error) {
      console.error('Error updating cart drawer contents:', error);
    }

    // After DOM replacement, only reorder when drawer is open; otherwise we reorder on open.
    document.dispatchEvent(new CustomEvent('CART_DRAWER_UPDATED'));
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
